import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Drawer,
    Empty,
    Input,
    Row,
    Skeleton,
    Space,
    Statistic,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    EditOutlined,
    GlobalOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SearchOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { ModelBeliefsAdhocForm } from './ModelBeliefsAdhocForm';
import { ModelBeliefsBrandConfigForm } from './ModelBeliefsBrandConfigForm';
import { ModelBeliefsHistoryChart } from './ModelBeliefsHistoryChart';
import { ModelBeliefsRunModal, ModelBeliefsRunTarget } from './ModelBeliefsRunModal';
import {
    aiAdminApi,
    ModelBeliefsAdhocConfigRow,
    ModelBeliefsBrandRow,
    ModelBeliefsBrandsResponse,
} from '../../../services/ai-admin-api';

const { Text, Paragraph } = Typography;

const when = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleString() : '-';

// Everything a read cannot invent lives in a brand's config, so this table is
// the answer to "why has this brand never been read": no row, or a row with no
// category label for the gate to ask about.
export const ModelBeliefsBrandsPanel: React.FC = () => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';

    const [data, setData] = useState<ModelBeliefsBrandsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [configuredOnly, setConfiguredOnly] = useState(false);
    const [editing, setEditing] = useState<ModelBeliefsBrandRow | null>(null);
    const [runTarget, setRunTarget] = useState<ModelBeliefsRunTarget | null>(null);
    const [adhocOpen, setAdhocOpen] = useState(false);
    // Brands that are not on the platform: configs with no brand. The same
    // editor and the same read as a platform brand.
    const [adhocConfigs, setAdhocConfigs] = useState<ModelBeliefsAdhocConfigRow[]>([]);
    const [editingConfig, setEditingConfig] = useState<ModelBeliefsAdhocConfigRow | null>(null);
    const loadAdhoc = useCallback(async () => {
        if (!jwt) return;
        try {
            setAdhocConfigs((await aiAdminApi.listModelBeliefsAdhocConfigs(jwt)).configs);
        } catch {
            setAdhocConfigs([]);
        }
    }, [jwt]);
    useEffect(() => {
        void loadAdhoc();
    }, [loadAdhoc]);
    const readAdhoc = (c: ModelBeliefsAdhocConfigRow) =>
        setRunTarget({
            kind: 'adhoc',
            configId: c.id,
            templateType: c.templateType ?? 'durable',
            target: {
                name: c.name ?? c.consumerName,
                consumerName: c.consumerName,
                websiteUrl: c.websiteUrl ?? (c.brandDomainsJson?.[0] ? `https://${c.brandDomainsJson[0]}` : ''),
                category: c.categoryLabel ?? '',
                country: c.marketCountry,
                city: c.marketCity ?? undefined,
                competitorNames: c.competitorNamesJson ?? [],
                disambiguation: c.disambiguationNote ?? undefined,
                pricePositioning: c.pricePositioning ?? null,
            },
        });

    const load = useCallback(
        async (term: string, onlyConfigured: boolean) => {
            if (!jwt) return;
            setLoading(true);
            setError(null);
            try {
                setData(
                    await aiAdminApi.listModelBeliefsBrands(jwt, {
                        search: term,
                        configuredOnly: onlyConfigured,
                        limit: 200,
                    }),
                );
            } catch (e) {
                setError((e as Error).message);
            } finally {
                setLoading(false);
            }
        },
        [jwt],
    );

    useEffect(() => {
        void load('', false);
    }, [load]);

    if (loading && !data) return <Skeleton active paragraph={{ rows: 8 }} />;
    if (error) return <Alert type="error" showIcon message={error} />;

    const columns = [
        {
            title: 'Brand',
            key: 'brand',
            width: 230,
            render: (_: unknown, row: ModelBeliefsBrandRow) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.brandName}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.organisationName ?? `Org ${row.organisationId}`} · brand{' '}
                        {row.brandId}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Config',
            key: 'config',
            width: 120,
            render: (_: unknown, row: ModelBeliefsBrandRow) => {
                if (!row.configured) return <Tag>not configured</Tag>;
                return row.readable ? (
                    <Tag color="green">ready</Tag>
                ) : (
                    <Tooltip title="Configured, but with no category label the awareness gate is skipped — and the gate is the first thing a read reports.">
                        <Tag color="orange">no category</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Category',
            key: 'category',
            render: (_: unknown, row: ModelBeliefsBrandRow) =>
                row.categoryLabel ? (
                    <Text style={{ fontSize: 13 }}>{row.categoryLabel}</Text>
                ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        -
                    </Text>
                ),
        },
        {
            title: 'Market',
            key: 'market',
            width: 90,
            render: (_: unknown, row: ModelBeliefsBrandRow) =>
                row.marketCountry ? (
                    <Space direction="vertical" size={0}>
                        <Text>{row.marketCountry}</Text>
                        {row.marketCity && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {row.marketCity}
                            </Text>
                        )}
                    </Space>
                ) : (
                    <Text type="secondary">-</Text>
                ),
        },
        {
            title: 'Competitors',
            dataIndex: 'competitorCount',
            key: 'competitorCount',
            width: 105,
            render: (value: number) => <Text>{value || '-'}</Text>,
        },
        {
            title: (
                <Tooltip title="Without a brand domain, the brand's own site is never counted as its own source and the impostor check flags it instead.">
                    <span>Domains</span>
                </Tooltip>
            ),
            dataIndex: 'brandDomainCount',
            key: 'brandDomainCount',
            width: 95,
            render: (value: number, row: ModelBeliefsBrandRow) =>
                value > 0 ? (
                    <Text>{value}</Text>
                ) : row.configured ? (
                    <Text type="warning">none</Text>
                ) : (
                    <Text type="secondary">-</Text>
                ),
        },
        {
            title: 'Last read',
            key: 'lastRun',
            width: 175,
            render: (_: unknown, row: ModelBeliefsBrandRow) =>
                row.lastRun ? (
                    <Space direction="vertical" size={0}>
                        <Text style={{ fontSize: 12 }}>
                            #{row.lastRun.id} · {row.lastRun.status}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {when(row.lastRun.completedAt ?? row.lastRun.createdAt)}
                        </Text>
                    </Space>
                ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        never read
                    </Text>
                ),
        },
        {
            title: '',
            key: 'actions',
            width: 215,
            render: (_: unknown, row: ModelBeliefsBrandRow) => (
                <Space size={4}>
                    <Button
                        size="small"
                        icon={<EditOutlined style={{ marginRight: 4 }} />}
                        onClick={() => setEditing(row)}
                    >
                        {row.configured ? 'Edit' : 'Configure'}
                    </Button>
                    <Tooltip
                        title={
                            row.readable
                                ? 'Estimate the read, then start it. Brand or admin run.'
                                : 'Needs a category label before it can be read.'
                        }
                    >
                        <Button
                            size="small"
                            icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                            disabled={!row.readable}
                            onClick={() =>
                                setRunTarget({ kind: 'brand', brandId: row.brandId, name: row.brandName })
                            }
                        >
                            Run a read
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={16}>
                <Col span={6}>
                    <Card>
                        <Statistic title="Configured" value={data?.configured ?? 0} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Ready to read"
                            value={
                                (data?.brands ?? []).filter((b) => b.readable).length
                            }
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card>
                        <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
                            A read asks the models about a category, in a market, and has
                            to know which domains are the brand&rsquo;s own. None of that
                            can be derived from the brand record, which is why it is
                            written down here. The config is frozen into a run when it is
                            enqueued, so an edit changes the next read, never one in
                            flight.
                        </Paragraph>
                    </Card>
                </Col>
            </Row>

            <Card
                title={
                    <Space>
                        <SettingOutlined style={{ marginRight: 8 }} />
                        Brand configs
                    </Space>
                }
                extra={
                    <Space>
                        <Input
                            allowClear
                            style={{ width: 240 }}
                            prefix={<SearchOutlined style={{ marginRight: 4 }} />}
                            placeholder="Brand or organisation"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onPressEnter={() => void load(search, configuredOnly)}
                        />
                        <Space size={4}>
                            <Switch
                                size="small"
                                checked={configuredOnly}
                                onChange={(checked) => {
                                    setConfiguredOnly(checked);
                                    void load(search, checked);
                                }}
                            />
                            <Text style={{ fontSize: 12 }}>Configured only</Text>
                        </Space>
                        <Button
                            icon={<ReloadOutlined style={{ marginRight: 4 }} />}
                            loading={loading}
                            onClick={() => void load(search, configuredOnly)}
                        >
                            Refresh
                        </Button>
                        <Tooltip title="A brand that is not on the platform: website, template, category from the site, competitors. Estimated before it runs; attachable to the brand if it signs up.">
                            <Button
                                icon={<GlobalOutlined style={{ marginRight: 4 }} />}
                                onClick={() => setAdhocOpen(true)}
                            >
                                Read a brand not on the platform
                            </Button>
                        </Tooltip>
                    </Space>
                }
                styles={{ body: { paddingTop: 0 } }}
            >
                <Table
                    rowKey="brandId"
                    size="small"
                    dataSource={data?.brands ?? []}
                    columns={columns as any}
                    pagination={{ pageSize: 25, hideOnSinglePage: true }}
                    locale={{ emptyText: <Empty description="No brands match" /> }}
                    expandable={{
                        // Only a brand that has been read has a history worth
                        // opening. The chart lives here rather than on the
                        // survey Trends chart on purpose: same scale, different
                        // instrument, and no shared calibration between them.
                        rowExpandable: (row: ModelBeliefsBrandRow) => !!row.lastRun,
                        expandedRowRender: (row: ModelBeliefsBrandRow) => (
                            <ModelBeliefsHistoryChart brandId={row.brandId} />
                        ),
                    }}
                />
                {(data?.total ?? 0) > (data?.brands.length ?? 0) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Showing {data?.brands.length} of {data?.total}. Search to narrow.
                    </Text>
                )}
            </Card>

            <Card
                title={
                    <Space>
                        <GlobalOutlined style={{ marginRight: 8 }} />
                        Not on the platform
                    </Space>
                }
                extra={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Targets read from the admin. Edit them here; attach a run to the brand when it signs up.
                    </Text>
                }
                styles={{ body: { paddingTop: 0 } }}
            >
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={adhocConfigs}
                    pagination={{ pageSize: 25, hideOnSinglePage: true }}
                    locale={{ emptyText: <Empty description="No targets yet. Use the button above to read one." /> }}
                    columns={[
                        {
                            title: 'Target',
                            key: 'name',
                            width: 230,
                            render: (_: unknown, c: ModelBeliefsAdhocConfigRow) => (
                                <Space direction="vertical" size={0}>
                                    <Text strong>{c.name ?? c.consumerName}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {c.websiteUrl ?? c.brandDomainsJson?.[0] ?? '-'}
                                        {c.templateType ? ` · ${c.templateType}` : ''}
                                    </Text>
                                </Space>
                            ),
                        },
                        {
                            title: 'Category',
                            key: 'category',
                            render: (_: unknown, c: ModelBeliefsAdhocConfigRow) =>
                                c.categoryLabel ? (
                                    <Text style={{ fontSize: 13 }}>{c.categoryLabel}</Text>
                                ) : (
                                    <Tag color="orange">no category</Tag>
                                ),
                        },
                        {
                            title: 'Market',
                            key: 'market',
                            width: 90,
                            render: (_: unknown, c: ModelBeliefsAdhocConfigRow) => (
                                <Space direction="vertical" size={0}>
                                    <Text>{c.marketCountry}</Text>
                                    {c.marketCity && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {c.marketCity}
                                        </Text>
                                    )}
                                </Space>
                            ),
                        },
                        {
                            title: 'Competitors',
                            key: 'competitors',
                            width: 105,
                            render: (_: unknown, c: ModelBeliefsAdhocConfigRow) => (
                                <Text>{c.competitorNamesJson?.length || '-'}</Text>
                            ),
                        },
                        {
                            title: 'Price',
                            key: 'price',
                            width: 90,
                            render: (_: unknown, c: ModelBeliefsAdhocConfigRow) =>
                                c.pricePositioning ? <Tag>{c.pricePositioning}</Tag> : <Text type="secondary">-</Text>,
                        },
                        {
                            title: 'Last read',
                            key: 'lastRun',
                            width: 175,
                            render: (_: unknown, c: ModelBeliefsAdhocConfigRow) =>
                                c.lastRun ? (
                                    <Space direction="vertical" size={0}>
                                        <Text style={{ fontSize: 12 }}>
                                            #{c.lastRun.id} · {c.lastRun.status}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {when(c.lastRun.completedAt ?? c.lastRun.createdAt)}
                                        </Text>
                                    </Space>
                                ) : (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        never read
                                    </Text>
                                ),
                        },
                        {
                            title: '',
                            key: 'actions',
                            width: 215,
                            render: (_: unknown, c: ModelBeliefsAdhocConfigRow) => (
                                <Space size={4}>
                                    <Button
                                        size="small"
                                        icon={<EditOutlined style={{ marginRight: 4 }} />}
                                        onClick={() => setEditingConfig(c)}
                                    >
                                        Edit
                                    </Button>
                                    <Tooltip
                                        title={
                                            c.categoryLabel
                                                ? 'Estimate the read, then start it. Platform spend.'
                                                : 'Needs a category label before it can be read.'
                                        }
                                    >
                                        <Button
                                            size="small"
                                            icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                                            disabled={!c.categoryLabel}
                                            onClick={() => readAdhoc(c)}
                                        >
                                            Run a read
                                        </Button>
                                    </Tooltip>
                                </Space>
                            ),
                        },
                    ]}
                />
            </Card>

            <Drawer
                width={880}
                open={!!editingConfig}
                destroyOnClose
                styles={{ body: { textAlign: 'left' } }}
                onClose={() => setEditingConfig(null)}
                title={editingConfig ? `${editingConfig.name ?? editingConfig.consumerName} · not on the platform` : ''}
            >
                {editingConfig && (
                    <ModelBeliefsBrandConfigForm
                        configId={editingConfig.id}
                        brandName={editingConfig.name ?? editingConfig.consumerName}
                        onSaved={() => void loadAdhoc()}
                    />
                )}
            </Drawer>

            <Drawer
                width={880}
                open={!!editing}
                destroyOnClose
                // App.css centre-aligns every .ant-drawer-body globally, which
                // reads as a form nobody proofread.
                styles={{ body: { textAlign: 'left' } }}
                onClose={() => setEditing(null)}
                title={
                    editing ? (
                        <Space direction="vertical" size={0}>
                            <Text strong>{editing.brandName}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {editing.organisationName ??
                                    `Org ${editing.organisationId}`}{' '}
                                · brand {editing.brandId}
                                {editing.templateType
                                    ? ` · ${editing.templateType}`
                                    : ''}
                            </Text>
                        </Space>
                    ) : null
                }
            >
                {editing && (
                    <ModelBeliefsBrandConfigForm
                        brandId={editing.brandId}
                        brandName={editing.brandName}
                        onSaved={() => void load(search, configuredOnly)}
                    />
                )}
            </Drawer>

            <Drawer
                width={880}
                open={adhocOpen}
                destroyOnClose
                styles={{ body: { textAlign: 'left' } }}
                onClose={() => setAdhocOpen(false)}
                title="Read a brand not on the platform"
            >
                <ModelBeliefsAdhocForm
                    onStarted={() => {
                        void load(search, configuredOnly);
                        void loadAdhoc();
                    }}
                />
            </Drawer>

            <ModelBeliefsRunModal
                open={!!runTarget}
                target={runTarget}
                onClose={() => setRunTarget(null)}
                onStarted={() => {
                    void load(search, configuredOnly);
                    void loadAdhoc();
                }}
            />
        </Space>
    );
};
